import { z } from 'zod';

const digits = (s: string) => s.replace(/\D/g, '');

export const contactSchema = z.object({
  name: z.string().trim().min(2, 'Укажите имя').max(80, 'Слишком длинное имя'),
  phone: z
    .string()
    .trim()
    .refine((v) => digits(v).length >= 10 && digits(v).length <= 15, 'Укажите номер с кодом страны, например +996 700 123 456'),
  notes: z.string().max(500, 'До 500 символов').default(''),
});
export type ContactInput = z.infer<typeof contactSchema>;

export function fieldErrors<T>(schema: z.ZodType<T>, value: unknown): Partial<Record<string, string>> {
  const r = schema.safeParse(value);
  if (r.success) return {};
  const out: Record<string, string> = {};
  for (const i of r.error.issues) {
    const k = String(i.path[0] ?? '_');
    out[k] ??= i.message;
  }
  return out;
}

/** "+7 (916) 111-22-33" for Russian numbers, untouched otherwise. */
export function formatPhone(raw: string): string {
  let d = digits(raw);
  if (d.length === 11 && d.startsWith('8')) d = `7${d.slice(1)}`;
  if (d.length === 10 && d.startsWith('9')) d = `7${d}`;
  if (d.length !== 11 || !d.startsWith('7')) return raw.trim();
  return `+7 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7, 9)}-${d.slice(9, 11)}`;
}

/**
 * Formats a phone while it is typed: "+996 700 12 34 56", "+998 90 123 45 67", "+7 900 123-45-67".
 * A number typed without "+" is local and gets the studio's country code (`code`, from dialCode).
 */
export function typePhone(raw: string, code: string): string {
  let d = digits(raw);
  if (!d) return '';
  if (code && !raw.trim().startsWith('+')) {
    if (code === '7' && d.length === 11 && d.startsWith('8')) d = d.slice(1);
    else if (d.startsWith('0')) d = d.slice(1);
    if (!d.startsWith(code)) d = code + d;
  }
  // "+996 0555…": the local trunk zero typed after the prefilled code.
  d = d.replace(/^(996|998)0/, '$1');
  const group = (prefix: string, rest: string, sizes: number[], seps: string[]) => {
    let out = prefix;
    let i = 0;
    sizes.forEach((n, k) => {
      if (i >= rest.length) return;
      out += (k ? seps[k - 1] : ' ') + rest.slice(i, i + n);
      i += n;
    });
    return out;
  };
  if (d.startsWith('996')) return group('+996', d.slice(3, 12), [3, 2, 2, 2], [' ', ' ', ' ']);
  if (d.startsWith('998')) return group('+998', d.slice(3, 12), [2, 3, 2, 2], [' ', ' ', ' ']);
  if (d.startsWith('7')) return group('+7', d.slice(1, 11), [3, 3, 2, 2], [' ', '-', '-']);
  return `+${d.slice(0, 15)}`;
}

/** «Номер короче нужного» for the formats typePhone knows (+996, +998, +7), else ''. */
export function shortPhone(v: string): string {
  const d = digits(v);
  const full = d.startsWith('996') || d.startsWith('998') ? 12 : d.startsWith('7') ? 11 : 0;
  return full && d.length < full ? 'Номер неполный — проверьте цифры' : '';
}

export const loginSchema = z.object({
  email: z.email('Проверьте почту'),
  password: z.string().min(6, 'Минимум 6 символов'),
});

export const moneySchema = z.coerce.number().positive('Сумма больше нуля').max(10_000_000, 'Слишком большая сумма');
